import DiscountIcon from "@mui/icons-material/Discount";
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import globalStyle from "../../globalStyle";
import {
  createDiscountRule,
  updateDiscountRule,
} from "../../services/discountRuleApiFn";
import { handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import AppLoader from "../AppLoader";
import Label from "../Label";
import TextInput from "../TextInputs";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";
import DialogHeader from "./Header";
import { addEditDiscountRuleValidation } from "../../utils/Validation";
import { DatePicker } from "@mui/x-date-pickers";
import { CURRENCY } from "../../constants/Variables";
import TranslateIcon from "@mui/icons-material/Translate";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const initialInputValues = {
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  minAmount: 0,
  discountAmount: 0,
  startDate: null,
  endDate: null,
};

const AddEditDiscountRuleDialog = forwardRef(({}, ref) => {
  const { t } = useTranslation(["catalogue", "common"]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState({
    save: false,
    translateToEnglish: false,
  });
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);

  // Redux State
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          name_en: data?.name?.en || "",
          name_ro: data?.name?.ro || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          minAmount: data?.minAmount || 0,
          discountAmount: data?.discountAmount || 0,
          startDate: data?.startDate ? dayjs(data?.startDate) : null,
          endDate: data?.endDate ? dayjs(data?.endDate) : null,
        });
      }
    },

    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
  };

  const handleAddEdit = () => {
    const valid = addEditDiscountRuleValidation(inputValues, setInputError);

    if (valid) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    let payload = {
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      minAmount: inputValues.minAmount,
      discountAmount: inputValues.discountAmount,
      startDate: inputValues.startDate,
      endDate: inputValues.endDate,
    };

    if (paramsData?.type === "add") {
      handleApiCall(createDiscountRule, payload, "");
    } else {
      handleApiCall(updateDiscountRule, payload, paramsData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData(searchKeys.discountRuleName);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.somethingWentWrong", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;

    setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  const handleDateChange = (date, name) => {
    setInputValues((pre) => ({ ...pre, [name]: date }));
    setInputError((pre) => ({ ...pre, [name]: "" }));
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        // fullWidth
        // maxWidth="md"
      >
        <DialogHeader
          title={
            paramsData.type === "add"
              ? t("addDiscountRule")
              : t("editDiscountRule")
          }
          secondaryHeading={
            paramsData.type === "add"
              ? t("addDiscountRuleInstruction")
              : t("editDiscountRuleInstruction")
          }
          icon={<DiscountIcon />}
          onClose={handleClose}
        />

        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("titleInRomanian")}
                htmlFor={`discount-rule-title-ro`}
              />
              <TextInput
                id={`discount-rule-title-ro`}
                required
                placeholder={t("enterTitleInRomanian")}
                name={`name_ro`}
                textLength={60}
                value={inputValues.name_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_ro)}
                helperText={t(inputError.name_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_ro?.length}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t("titleInEnglish")}
                  htmlFor={`discount-rule-title-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() => handleTranslateToEnglish("name_ro", "name_en")}
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`discount-rule-title-en`}
                required
                placeholder={t("enterTitleInEnglish")}
                name={`name_en`}
                textLength={60}
                value={inputValues.name_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.name_en)}
                helperText={t(inputError.name_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length || 0}/60
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  optional
                  labelText={t("descriptionInRomanian")}
                  htmlFor={`discount-rule-description-ro`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Typography variant="body2" color="text.grey">
                  {inputValues.description_ro?.length || 0}/300
                </Typography>
              </Stack>
              <TextInput
                id={`discount-rule-description-ro`}
                placeholder={t("enterDescriptionInRomanian")}
                name={`description_ro`}
                textLength={300}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_ro)}
                helperText={t(inputError.description_ro)}
                multiline={true}
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  optional
                  labelText={t("descriptionInEnglish")}
                  htmlFor={`discount-rule-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      gap={1}
                      width={"100%"}
                    >
                      <Typography variant="body2">
                        {t("translateToEnglish", { ns: "common" })}
                      </Typography>
                      <Typography variant="body2" color="text.grey">
                        {inputValues.description_en?.length || 0}/300
                      </Typography>
                    </Stack>
                  }
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`discount-rule-description-en`}
                placeholder={t("enterDescriptionInEnglish")}
                name={`description_en`}
                textLength={300}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.description_en)}
                helperText={t(inputError.description_en)}
                multiline={true}
                rows={4}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("minAmount")}
                htmlFor={"min-amount"}
              />
              <TextInput
                id={"min-amount"}
                styleProps={globalStyle.textField}
                placeholder={t("enterMinAmount")}
                required
                name={"minAmount"}
                type={"number"}
                value={inputValues.minAmount}
                onChange={handleChageInputs}
                inputError={inputError.minAmount}
                helperText={t(inputError.minAmount)}
                InputEndIcon={CURRENCY}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("discountAmount")}
                htmlFor={"discount-amount"}
              />
              <TextInput
                id={"discount-amount"}
                styleProps={globalStyle.textField}
                placeholder={t("enterDiscountAmount")}
                required
                name={"discountAmount"}
                type={"number"}
                value={inputValues.discountAmount}
                onChange={handleChageInputs}
                inputError={inputError.discountAmount}
                helperText={t(inputError.discountAmount)}
                InputEndIcon={CURRENCY}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("startDate")}
                htmlFor={"start-date"}
              />
              <DatePicker
                id={"start-date"}
                sx={{ width: "100%", ...globalStyle.textField }}
                name={"startDate"}
                value={inputValues.startDate}
                onChange={(date) => handleDateChange(date, "startDate")}
                slotProps={{
                  textField: {
                    error: inputError.startDate,
                    helperText: t(inputError.startDate),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label required labelText={t("endDate")} htmlFor={"end-date"} />
              <DatePicker
                id={"end-date"}
                sx={{ width: "100%", ...globalStyle.textField }}
                minDate={inputValues.startDate}
                name={"endDate"}
                value={inputValues.endDate}
                onChange={(date) => handleDateChange(date, "endDate")}
                slotProps={{
                  textField: {
                    error: inputError.endDate,
                    helperText: t(inputError.endDate),
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEdit}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});

export default AddEditDiscountRuleDialog;
